import { useState, useEffect, useRef } from "react";
import { useStatementOperationsAndInterests } from "@/services/admin-services/query/useStatementOperationsAndInterests";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Layer, Customized, Brush, ReferenceArea } from "recharts";
import { Snowflake } from "lucide-react";
import PrintErrorMessage from "@/components/organisms/feedback/PrintErrorMessage";
import { Label } from "@/components/atoms/texts/label";
// import { Button } from "@/components/molecules/buttons/button";
// import { CloseRounded } from "@mui/icons-material";
import React from "react";
// import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import PrintInformationMessage from "../feedback/PrintInformationMessage";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InterestsHoldPeriod } from "@/components/molecules/InterestsHoldIcon";
import { clipInterestsHoldToRange, findInterestsHoldForDate } from "@/utils/interestsHolds";
// import "./auto-scroll.css";

// Fonction utilitaire pour formater la date et les montants
function formatDateFR(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR");
}
function formatAmountFR(amount) {
    if (amount === undefined || amount === null) return "";
    return amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Définition des couleurs utilisées dans le composant
const COLOR_GREEN = "#27AE60";
const COLOR_ORANGE = "#f49236";
const COLOR_RED = "#f44336";
const COLOR_PURPLE = "#a362f8";
const COLOR_BLUE = "hsl(var(--secondary))";
const COLOR_HOLD = "#38bdf8";

// Tooltip personnalisé
function CustomTooltip({ active, payload, label, currency, dataOwner }) {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;
    console.log("Tooltip data:", data);

    // Ne pas afficher le tooltip si pas d'opérations (inclut le cas du 1er janvier filtré)
    const hasOps = data.operations && data.operations.length > 0;
    const opsRef = useRef(null);
    useAutoScroll(opsRef, hasOps, data.operations, 25, 500, 300, 1000); // speed=0.3, pauseBottom=500ms, pauseTop=300ms

    return (
        <div
            className="p-2 bg-white border rounded shadow -translate-y-full"
            style={{
                // marginTop: "24px", // Décale le tooltip vers le haut
                // transform: "translateY(-86px)", // Décale le tooltip vers le haut
                zIndex: 1000,
                position: "relative"
            }}
        >
            <div className="font-bold">
                <Label>{formatDateFR(data.date)}</Label>
            </div>
            {hasOps ? (
                <>
                    <div
                        ref={opsRef}
                        className="overflow-y-auto max-h-20 auto-scroll"
                    >
                        {data.operations.map((op, idx) => (
                            <div key={op.uuid} className="mb-1 flex gap-firstDegree items-center">
                                <Label message={`consultants.statement.statementOperations.statementOperationType_` + op.operationType} >{op.operationType}</Label> :
                                <Label>{op.amount > 0 ? "+" : ""}{formatAmountFR(op.amount)} {currency}</Label>
                            </div>
                        ))}
                    </div>
                    <hr className="my-1" />

                    <div >
                        <Label message="consultants.statement.statementOperations.overflowValue" /> : <Label fontWeight="semibold" className={data.overflowValue < 0 ? `text-[#f44336]` : data.overflowValue <= data.employerCostExclBonus ? `text-[#f49236] ` : `text-[#27AE60]`}>{formatAmountFR(data.overflowValue)} {currency}</Label>
                    </div>

                    {(data?.interestEur && data?.interestEur != 0) ?
                        <div className="bg-[#a362f8] text-white px-firstDegree rounded-sm font-semibold">
                            <Label message="consultants.statement.statementOperations.pendingInterest" /> : <Label>{formatAmountFR(data?.interestEur)} {currency}</Label>
                        </div> : <></>}
                    {(data?.employerCostExclBonus && data?.employerCostExclBonus != 0) ? <div >
                        <Label message="consultants.statement.statementOperations.minOverflowForInterets" /> : <Label className={` ${data.overflowValue <= data.employerCostExclBonus && `text-secondary font-semibold`}`}>{formatAmountFR(data.employerCostExclBonus)} {currency}</Label>
                    </div> : <></>}
                </>
            ) : (
                <>
                    {<div >
                        <Label message="consultants.statement.statementOperations.overflowValue" /> : <Label fontWeight="semibold" className={data.overflowValue < 0 ? `text-[#f44336]` : data.overflowValue <= data.employerCostExclBonus ? `text-[#f49236] ` : `text-[#27AE60]`}>
                            {formatAmountFR(data.overflowValue)} {currency}</Label>

                    </div>}
                    {(data?.interestEur && data?.interestEur != 0) ?
                        <div className="bg-[#a362f8] text-white px-firstDegree rounded-sm font-semibold">
                            <Label message="consultants.statement.statementOperations.pendingInterest" /> : <Label>{formatAmountFR(data?.interestEur)} {currency}</Label>
                        </div> : <></>}
                    {/* {formatAmountFR(data?.interestEur)} */}
                    {(data?.employerCostExclBonus && data?.employerCostExclBonus != 0) ? <div >
                        <Label message="consultants.statement.statementOperations.minOverflowForInterets" /> : <Label className={` ${(data.overflowValue <= data.employerCostExclBonus) && `text-secondary font-semibold`}`}>{formatAmountFR(data.employerCostExclBonus)} {currency}</Label>
                    </div> : <></>}
                </>
            )}
            {data.interestsHold && (
                <div className="mt-1 flex flex-col gap-betweenText border-t pt-1">
                    <span className="flex items-center gap-betweenText" style={{ color: COLOR_HOLD }}>
                        <Snowflake className="h-4 w-4" />
                        <Label fontWeight="semibold" message="consultants.statement.interestsHolds.frozenDay" />
                    </span>
                    <InterestsHoldPeriod hold={data.interestsHold} />
                    {data.interestsHold.comment && <Label size="small" color="disabled">{data.interestsHold.comment}</Label>}
                </div>
            )}
        </div>
    );
}

// Zone de gel : icône en haut à gauche de la bande
const InterestsHoldAreaLabel = ({ viewBox }) => {
    if (!viewBox || viewBox.width < 14) return null;
    return <Snowflake x={viewBox.x + 4} y={viewBox.y + 4} size={14} color={COLOR_HOLD} />;
};

// Légende personnalisée
const CustomLegend = ({ showHolds }) => (
    <div className="flex flex-row justify-center gap-thirdDegree">
        {/* Losange vert/rouge pour overflowValue */}
        {/* <span style={{ display: "flex", alignItems: "center", gap: 6 }}> */}
        <div className="flex items-center justify-center gap-firstDegree">
            <svg width="16" height="16" viewBox="0 0 15 15">
                {/* Haut vert */}
                <polygon points="8,0 15,7 1,7" fill={COLOR_GREEN} />
                {/* Milieu orange */}
                <polygon points="1,7 15,7 15,8 1,8" fill={COLOR_ORANGE} />
                {/* Bas rouge */}
                <polygon points="8,8 15,8 8,15 1,8" fill={COLOR_RED} />
            </svg>
            <span >
                <Label message="consultants.statement.statementOperations.overflowValue" />
            </span>
        </div>
        {/* </span> */}
        {/* Dot jaune pour intérêts */}
        <div className="flex items-center justify-center gap-firstDegree">
            <svg width="32" height="18">
                {/* Ligne horizontale */}
                <line x1="2" y1="11" x2="30" y2="11" stroke={COLOR_PURPLE} strokeWidth="2" />
                {/* Cercle jaune par-dessus */}
                <circle cx="16" cy="11" r="5" fill={COLOR_PURPLE} stroke={COLOR_PURPLE} strokeWidth="2" />
            </svg>
            <span >
                <Label message="consultants.statement.statementOperations.interestEur" />
            </span>
        </div>
        {/* Ligne pointillée rouge pour employerCostExclBonus */}
        <div className="flex items-center justify-center gap-firstDegree">
            <svg width="28" height="8">
                <line x1="4" y1="6" x2="26" y2="6" stroke={COLOR_BLUE} strokeWidth="2" strokeDasharray="4,4" />
            </svg>
            <span >
                <Label message="consultants.statement.statementOperations.minOverflowForInterets" />
            </span>
        </div>
        {showHolds && (
            <div className="flex items-center justify-center gap-firstDegree">
                <span className="flex items-center justify-center w-7 h-4 rounded-sm" style={{ backgroundColor: `${COLOR_HOLD}33`, color: COLOR_HOLD }}>
                    <Snowflake className="h-3 w-3" />
                </span>
                <Label message="consultants.statement.interestsHolds.frozenPeriod" />
            </div>
        )}
    </div>
);

export default function StatementOperationsChart({ year, yearStatement, dataOwner = false, lightStyle = false, interestsHolds = [], onHoldClick }) {
    // Ajout du toggle mois
    const now = new Date();
    const [t] = useTranslation("global");
    const [id] = useParams().id ? [useParams().id] : [null];
    const currentMonth = now.getMonth(); // 0-indexed
    const monthsFr = [
        t("global.months.january"),
        t("global.months.february"),
        t("global.months.march"),
        t("global.months.april"),
        t("global.months.may"),
        t("global.months.june"),
        t("global.months.july"),
        t("global.months.august"),
        t("global.months.september"),
        t("global.months.october"),
        t("global.months.november"),
        t("global.months.december")
    ];
    // Liste des valeurs pour le toggle (mois jusqu'au mois courant + "cette année")
    const toggleValues = Array.from({ length: currentMonth + 1 }, (_, i) => ({
        value: (i + 1).toString(),
        label: monthsFr[i]
    }));
    toggleValues.push({ value: "all", label: "cette année" });

    const [selectedMonth, setSelectedMonth] = useState("all");

    // On récupère toutes les données de l'année pour détecter les mois avec données
    const { data: yearData, loading: yearLoading, error: yearError } = useStatementOperationsAndInterests(year, null, dataOwner, id);
    const monthParam = selectedMonth === "all" ? null : Number(selectedMonth);
    const { data, loading, error } = useStatementOperationsAndInterests(year, monthParam, dataOwner, id);
    const currency = "€" //TODO à variabiliser

    // Détection des mois avec données à partir de la réponse yearData
    useEffect(() => {
        if (!yearData?.days) return;
        const monthsData = {};
        // On parcourt tous les jours et on marque les mois qui ont au moins un jour
        yearData.days.forEach(day => {
            const d = new Date(day.day);
            const month = d.getMonth() + 1;
            monthsData[month] = true;
        });
        // On complète pour tous les mois jusqu'au mois courant
        for (let i = 1; i <= currentMonth + 1; i++) {
            if (!monthsData[i]) monthsData[i] = false;
        }
        setMonthsWithData(monthsData);
    }, [yearData, currentMonth]);

    // Ajout de l'initialisation pour éviter undefined
    const [monthsWithData, setMonthsWithData] = useState({});

    // Gestion du changement de sélection du ToggleGroup
    const handleMonthChange = (value) => {
        // Si on clique sur le mois déjà sélectionné (hors 'all'), on sélectionne 'all'
        if (value === selectedMonth && value !== "all") {
            setSelectedMonth("all");
        } else if (value === null || value === undefined || value === "") {
            setSelectedMonth("all");
        } else {
            setSelectedMonth(value);
        }
    };

    if (data?.days.length == 0 && (loading || yearLoading)) return <span className="loader" />
    if (error || yearError) return <PrintErrorMessage error={error || yearError} />;
    // if (!data?.days?.length) return null;

    // Affichage spécifique si un seul point
    if (data?.days.length === 1 && !selectedMonth) {
        const day = data.days[0];
        const date = day.day;
        const operations = day.statementOperations || [];
        let afterBalance = null;
        if (operations.length > 0) {
            afterBalance = operations[operations.length - 1].afterBalance;
        }
        return (
            <Card className="w-full rounded-sm border-0 bg-transparent backdrop-blur-md p-0">
                <CardContent className="p-firstDegree">
                    <div className="flex flex-col gap-secondDegree bg-blue-100 p-firstDegree rounded-sm">

                        <div className="flex flex-col gap-firstDegree items-center justify-between">
                            <span className="flex gap-betweenText">
                                <Label message="consultants.statement.statementOperations.soldeDate" />
                                <Label>{formatDateFR(date)}</Label>
                            </span>
                            <Label color="primary" fontWeight="bold" size="h4">{formatAmountFR(afterBalance)}</Label>
                            <div className="flex gap-betweenText text-bodySmall">
                                {/* <Label message="consultants.statement.statementOperations.operations" /> : */}
                                {operations.length === 1 && < >
                                    <Label message={`consultants.statement.statementOperations.statementOperationType_` + operations[0].operationType} className="text-bodySmall" /> <Label className="text-bodySmall">{formatDateFR(operations[0].valueDate)} : </Label> <Label className="text-bodySmall" fontWeight="semibold">{operations[0].amount > 0 ? "+" : ""}{formatAmountFR(operations[0].amount)} €</Label>
                                </>}
                            </div>
                        </div>

                    </div>
                </CardContent>
            </Card>
        );
    }



    // Préparation des données pour le graphique - Compléter les jours manquants
    let completeDays = [];
    if (data?.days?.length) {
        // Créer un Map pour accès rapide aux données existantes par date
        const daysMap = new Map();
        data.days.forEach(day => {
            daysMap.set(day.day, day);
        });

        // Trouver la date la plus ancienne
        // Parsing local (évite le décalage UTC qui fait glisser la date d'un jour en timezone UTC+x)
        const parseLocalDate = (str) => { const [y, m, d] = str.split('-').map(Number); return new Date(y, m - 1, d); };
        const sortedDates = data.days.map(d => parseLocalDate(d.day)).sort((a, b) => a - b);
        const firstDate = sortedDates[0];

        // Déterminer la date de fin selon l'année des données
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Réinitialiser l'heure pour la comparaison
        const currentYear = today.getFullYear();
        const dataYear = firstDate.getFullYear();

        let endDate;
        if (dataYear < currentYear) {
            // Pour les années passées, s'arrêter au 31 décembre de cette année
            endDate = new Date(dataYear, 11, 31); // 11 = décembre (0-indexed)
            endDate.setHours(23, 59, 59, 999);
        } else {
            // Pour l'année en cours, s'arrêter à aujourd'hui
            endDate = today;
        }

        // Créer une entrée pour chaque jour entre le premier jour et la date de fin
        const currentDate = new Date(firstDate);
        let safetyCounter = 0;
        const MAX_ITERATIONS = 366; // Protection contre boucle infinie (1 année bissextile max)

        while (currentDate <= endDate && safetyCounter < MAX_ITERATIONS) {
            const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
            if (daysMap.has(dateStr)) {
                // Jour existant avec données
                completeDays.push(daysMap.get(dateStr));
            } else {
                // Jour manquant - créer une entrée vide
                completeDays.push({
                    day: dateStr,
                    statementOperations: [],
                    statementInterests: null
                });
            }
            currentDate.setDate(currentDate.getDate() + 1);
            safetyCounter++;
        }

        // Log d'avertissement si la limite est atteinte
        if (safetyCounter >= MAX_ITERATIONS) {
            console.warn('Limite de sécurité atteinte lors de la génération des jours complétés');
        }
    }

    let lastBalance = null;
    let lastInterest = null;
    const chartData = [];
    const statementMonths = yearStatement?.statementMonths || [];
    let prevOverflowValue = null;
    let prevEmployerCostExclBonus = null;

    // --- Ajout pour ticks mensuels sur l'axe X ---
    // On extrait les premiers jours de chaque mois présent dans les données
    let monthTicks = [];
    if (completeDays.length) {
        const seenMonths = new Set();
        completeDays.forEach(day => {
            const d = new Date(day.day);
            const monthKey = `${d.getFullYear()}-${d.getMonth()}`;
            if (!seenMonths.has(monthKey)) {
                seenMonths.add(monthKey);
                monthTicks.push(day.day);
            }
        });
    }
    // Tableau des abréviations de mois
    const monthsShortFr = [
        t("global.monthsShort.januaryShort"),
        t("global.monthsShort.februaryShort"),
        t("global.monthsShort.marchShort"),
        t("global.monthsShort.aprilShort"),
        t("global.monthsShort.mayShort"),
        t("global.monthsShort.juneShort"),
        t("global.monthsShort.julyShort"),
        t("global.monthsShort.augustShort"),
        t("global.monthsShort.septemberShort"),
        t("global.monthsShort.octoberShort"),
        t("global.monthsShort.novemberShort"),
        t("global.monthsShort.decemberShort")
    ];
    // Formatter pour afficher le mois uniquement sur les ticks mensuels
    function monthTickFormatter(dateStr) {
        const d = new Date(dateStr);
        return monthsShortFr[d.getMonth()];
    }

    completeDays.forEach((day, idx) => {
        let overflowUnderMinimum = lastBalance;
        let interestEur = lastInterest;
        let operations = [];
        if (day.statementOperations?.length > 0) {
            overflowUnderMinimum = day.statementOperations[day.statementOperations.length - 1].afterBalance;
            operations = day.statementOperations;

            // Filtrer les opérations à 0€ du 1er janvier
            const dayDate = new Date(day.day);
            if (dayDate.getDate() === 1 && dayDate.getMonth() === 0) {
                const filteredOps = operations.filter(op => op.amount !== 0);
                // Si toutes les opérations sont à 0€, on vide le tableau
                // Sinon on garde uniquement les opérations non nulles
                operations = filteredOps;
            }
        }
        if (day.statementInterests) {
            interestEur = day.statementInterests.interestsBalanceEur
            // (day.statementInterests.accruedInterestsEur || 0) +
            // (day.statementInterests.paidInterestsEur || 0) +
            // (day.statementInterests.interestsBalanceEur || 0);
        }
        let employerCostExclBonus = undefined;
        if (statementMonths.length) {
            const d = new Date(day.day);
            const month = d.getMonth() + 1;
            const monthObj = statementMonths.find(m => m.month === month);
            employerCostExclBonus = monthObj ? (2 * monthObj.employerCostExclBonus) : undefined;
        }

        let overflowValue = overflowUnderMinimum;
        let showOverflowNegative = null;
        let showOverflowUnderMinimum = null;
        let showOverflowUpperMinimum = null;

        // Logique à trois segments :
        // 1. Rouge si overflow < 0
        // 2. Orange si 0 <= overflow < employerCostExclBonus
        // 3. Vert si overflow >= employerCostExclBonus

        // Gestion des transitions entre segments
        if (
            prevOverflowValue !== null &&
            prevEmployerCostExclBonus !== null
        ) {
            // Transition de vert vers orange
            if (prevOverflowValue >= prevEmployerCostExclBonus && overflowValue < employerCostExclBonus && overflowValue >= 0) {
                showOverflowUnderMinimum = overflowUnderMinimum;
                showOverflowUpperMinimum = overflowUnderMinimum;
            }
            // Transition d'orange vers vert
            else if (prevOverflowValue < prevEmployerCostExclBonus && prevOverflowValue >= 0 && overflowValue >= employerCostExclBonus) {
                showOverflowUnderMinimum = overflowUnderMinimum;
                showOverflowUpperMinimum = overflowUnderMinimum;
            }
            // Transition de rouge vers orange
            else if (prevOverflowValue < 0 && overflowValue >= 0 && overflowValue < employerCostExclBonus) {
                showOverflowNegative = overflowUnderMinimum;
                showOverflowUnderMinimum = overflowUnderMinimum;
            }
            // Transition d'orange vers rouge
            else if (prevOverflowValue >= 0 && prevOverflowValue < prevEmployerCostExclBonus && overflowValue < 0) {
                showOverflowNegative = overflowUnderMinimum;
                showOverflowUnderMinimum = overflowUnderMinimum;
            }
            // Transition de rouge vers vert (cas rare)
            else if (prevOverflowValue < 0 && overflowValue >= employerCostExclBonus) {
                showOverflowNegative = overflowUnderMinimum;
                showOverflowUnderMinimum = overflowUnderMinimum;
                showOverflowUpperMinimum = overflowUnderMinimum;
            }
            // Transition de vert vers rouge (cas rare)
            else if (prevOverflowValue >= prevEmployerCostExclBonus && overflowValue < 0) {
                showOverflowNegative = overflowUnderMinimum;
                showOverflowUnderMinimum = overflowUnderMinimum;
                showOverflowUpperMinimum = overflowUnderMinimum;
            }
            // Cas normaux sans transition
            else if (overflowValue < 0) {
                showOverflowNegative = overflowUnderMinimum;
            } else if (overflowValue >= 0 && overflowValue < employerCostExclBonus) {
                showOverflowUnderMinimum = overflowUnderMinimum;
            } else if (overflowValue >= employerCostExclBonus) {
                showOverflowUpperMinimum = overflowUnderMinimum;
            }
        } else {
            // Premier point ou données manquantes
            if (overflowValue < 0) {
                showOverflowNegative = overflowUnderMinimum;
            } else if (overflowValue !== undefined && employerCostExclBonus !== undefined && overflowValue >= employerCostExclBonus) {
                showOverflowUpperMinimum = overflowUnderMinimum;
            } else {
                showOverflowUnderMinimum = overflowUnderMinimum;
            }
        }

        chartData.push({
            date: day.day,
            overflowValue,
            overflowNegative: showOverflowNegative,
            overflowUnderMinimum: showOverflowUnderMinimum,
            overflowUpperMinimum: showOverflowUpperMinimum,
            interestEur,
            operations,
            employerCostExclBonus,
            interestsHold: findInterestsHoldForDate(day.day, interestsHolds),
        });

        prevOverflowValue = overflowValue;
        prevEmployerCostExclBonus = employerCostExclBonus;
        lastBalance = overflowUnderMinimum;
        lastInterest = interestEur;
    });

    const holdAreas = chartData.length
        ? interestsHolds
            .map((hold) => ({ hold, range: clipInterestsHoldToRange(hold, chartData[0].date, chartData[chartData.length - 1].date) }))
            .filter(({ range }) => range)
        : [];

    // Custom dot pour afficher chaque opération (sans interaction)
    const OperationDot = (props) => {
        const { cx, cy, payload } = props;
        const [hoveredIdx, setHoveredIdx] = useState(null);

        // Ne pas afficher de point si pas d'opérations (inclut le cas du 1er janvier filtré)
        if (!payload.operations || payload.operations.length === 0) return null;

        // Déterminer la couleur en fonction de la valeur de l'overflow
        let color = COLOR_RED; // Par défaut rouge
        if (payload.overflowValue >= 0) {
            if (payload.employerCostExclBonus !== undefined && payload.overflowValue >= payload.employerCostExclBonus) {
                color = COLOR_GREEN;
            } else {
                color = COLOR_ORANGE;
            }
        }

        return payload.operations.map((op, idx) => {
            const size = hoveredIdx === idx ? 16 : 8;
            return (
                <svg
                    key={op.uuid}
                    x={cx - size / 2}
                    y={cy - size / 2}
                    width={size}
                    height={size}
                    style={{
                        cursor: "default",
                        zIndex: 1000,
                        overflow: "visible"
                    }}
                // Suppression des handlers d'interaction
                >
                    <polygon
                        points={`${size / 2},0 ${size},${size / 2} ${size / 2},${size} 0,${size / 2}`}
                        fill={color}
                    />
                </svg>
            );
        });
    };

    // Ajout du ToggleGroup au début du rendu
    return (
        <Card className="w-full  rounded-sm border-0 bg-transparent backdrop-blur-md p-0 ">



            <CardContent className="overflow-visible p-firstDegree">
                {!data?.days?.length ?
                    // <div className="h-[300px]">
                        (yearLoading || loading) ? <span className="loader flex" ></span> : <PrintInformationMessage information="error.emptyoverflow" />
                    // </div>
                    : (
                        <ResponsiveContainer width="100%" height={300} className="overflow-visible">
                            <LineChart data={chartData}>
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={monthTickFormatter}
                                    ticks={monthTicks}
                                    style={{ fontSize: "14px", fontFamily: "Montserrat, sans-serif" }}
                                    // height={lightStyle && 0}
                                    axisLine={!lightStyle}
                                    label={<Label message="consultants.statement.statementOperations.date" />}
                                />
                                <YAxis
                                    yAxisId="left"
                                    tickFormatter={formatAmountFR}
                                    // tick={<CustomAmountTick />}
                                    style={{ fontSize: "14px", fontFamily: "Montserrat, sans-serif" }}
                                    width={lightStyle ? 0 : 80}
                                    axisLine={!lightStyle}

                                    label={<Label message="consultants.statement.statementOperations.solde" />}
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tickFormatter={formatAmountFR}
                                    // tick={<CustomAmountTick />}
                                    style={{ fontSize: "14px", fontFamily: "Montserrat, sans-serif" }}
                                    tick={{ fill: COLOR_PURPLE }}
                                    width={lightStyle ? 0 : 80}
                                    label={<Label message="consultants.statement.statementOperations.interets" />}
                                />
                                <Tooltip
                                    content={<CustomTooltip currency={currency} />}
                                />
                                {!lightStyle && <Legend content={<CustomLegend showHolds={holdAreas.length > 0} />} />}
                                {holdAreas.map(({ hold, range }) => (
                                    <ReferenceArea
                                        key={hold.uuid}
                                        x1={range.x1}
                                        x2={range.x2}
                                        yAxisId="left"
                                        fill={COLOR_HOLD}
                                        fillOpacity={0.15}
                                        stroke={COLOR_HOLD}
                                        strokeOpacity={0.4}
                                        label={<InterestsHoldAreaLabel />}
                                        onClick={onHoldClick ? () => onHoldClick(hold) : undefined}
                                        style={onHoldClick ? { cursor: "pointer" } : undefined}
                                    />
                                ))}
                                <Line
                                    type="monotone"
                                    dataKey="overflowNegative"
                                    name="overflowNegative"
                                    yAxisId="left"
                                    dot={false}
                                    stroke={COLOR_RED}
                                    legendType="none"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="overflowUnderMinimum"
                                    name="overflowUnderMinimum"
                                    yAxisId="left"
                                    dot={false}
                                    stroke={COLOR_ORANGE}
                                    legendType="none"

                                />
                                <Line
                                    type="monotone"
                                    dataKey="overflowValue"
                                    name="overflowValue"
                                    yAxisId="left"
                                    dot={<OperationDot />}
                                    activeDot={<OperationDot />}
                                    strokeWidth={0}
                                    legendType="none"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="overflowUpperMinimum"
                                    name="overflowUpperMinimum"
                                    yAxisId="left"
                                    dot={false}
                                    activeDot={false}
                                    stroke={COLOR_GREEN}
                                    legendType="none"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="interestEur"
                                    stroke={COLOR_PURPLE}
                                    name="interestEur"
                                    legendType="none"
                                    yAxisId="right"
                                    dot={false}

                                />
                                {/* Ligne horizontale pour employerCostExclBonus */}
                                <Line
                                    type="stepAfter"
                                    dataKey="employerCostExclBonus"
                                    stroke={COLOR_BLUE}
                                    name="minOverflowForInterets"
                                    yAxisId="left"
                                    dot={false}
                                    strokeDasharray="4 2"
                                    legendType="none"
                                    isAnimationActive={false}
                                    radius={[4, 4, 0, 0]}

                                />
                                {!lightStyle && (
                                    <Brush
                                        dataKey="date"
                                        height={20}
                                        stroke="hsl(var(--transparent))"
                                        fill="hsl(var(--information))"
                                        tickFormatter={monthTickFormatter}
                                        alwaysShowText
                                        travellerWidth={10}
                                        traveller={<rect height={20}  rx={0} fill="hsl(var(--information))" />}
                                    />
                                )}
                            </LineChart>
                        </ResponsiveContainer>)}
            </CardContent>
            {/* {!lightStyle && <div className="flex justify-center mb-2">
                <ToggleGroup
                    value={selectedMonth}
                    onValueChange={handleMonthChange}
                    defaultValue="all"
                    type="single"
                    className="z-10  border border-lighter bg-lighter mr-firstDegree"
                >
                    {toggleValues.map(({ value, label }) => (
                        <ToggleGroupItem
                            key={value}
                            value={value}
                            className="rounded-sm gap-firstDegree"
                            disabled={value !== "all" && !monthsWithData ? true : (value !== "all" && !monthsWithData[Number(value)])}
                        >
                            <Label>{label}</Label>
                        </ToggleGroupItem>
                    ))}
                </ToggleGroup>
            </div>} */}
        </Card>
    );
}

// Fonction utilitaire pour scroll auto en boucle
function useAutoScroll(ref,
                       enabled = true,
                       content = null,
                       speedPxPerSec = 25,
                       pauseBottom = 500,
                       pauseTop = 300,
                       initialPause = 1000) {
    useEffect(() => {
        const element = ref.current;
        // Optimisation : on ne lance pas l'animation si :
        // 1. Le hook est désactivé ou la ref est vide
        // 2. Le contenu est plus petit ou égal au conteneur (scroll inutile)
        if (!enabled || !element || element.scrollHeight <= element.clientHeight) return;

        // Reset : On remet le scroll en haut au changement de contenu du tooltip (dependency content)
        element.scrollTop = 0;

        let requestID;
        let lastTime = null;
        let direction = 1; // 1 = down, -1 = up
        let waitTill = 0; // Timestamp jusqu'auquel on attend (pause)
        let accumulator = 0; // Accumule les fragments de pixels

        const animate = (time) => {
            if (lastTime === null) {
                lastTime = time;
                // On définit une pause initiale avant de démarrer le scroll
                waitTill = time + initialPause;
            }
            const delta = time - lastTime;
            lastTime = time;

            // Si une pause est active, on attend
            if (time < waitTill) {
                requestID = requestAnimationFrame(animate);
                return;
            }

            // Calcul du déplacement théorique basé sur le temps écoulé
            // (pixels/sec / 1000) * ms écoulées
            const move = direction * (speedPxPerSec / 1000) * delta;
            accumulator += move;

            // Application du scroll uniquement quand on a accumulé au moins 1 pixel entier
            // (nécessaire car scrollTop peut arrondir ou ignorer les sous-pixels selon le navigateur)
            if (Math.abs(accumulator) >= 1) {
                const pixelsToScroll = Math.trunc(accumulator);
                element.scrollTop += pixelsToScroll;
                accumulator -= pixelsToScroll;
            }

            // Vérifier si on est arrivé en bas
            if (direction === 1 && element.scrollTop + element.clientHeight >= element.scrollHeight - 1) {
                direction = -1;
                waitTill = time + pauseBottom;
                accumulator = 0;
            }
            // Vérifier si on est arrivé en haut
            else if (direction === -1 && element.scrollTop <= 0) {
                direction = 1;
                waitTill = time + pauseTop;
                accumulator = 0;
            }

            requestID = requestAnimationFrame(animate);
        };

        requestID = requestAnimationFrame(animate);

        return () => cancelAnimationFrame(requestID);
    }, [ref, enabled, content, speedPxPerSec, pauseBottom, pauseTop, initialPause]);
}
